import { useCallback, useEffect, useMemo, useRef, type MouseEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import api from '../api/client'
import { getProfile } from '../api/auth'
import { useAuth } from '../context/AuthContext'
import { authUrl, consumeReturn } from '../utils/authReturn'
import { backgroundOf, isAuthModalPath } from '../utils/routeModal'

type AuthPath = '/login' | '/register'

interface AuthOpenOptions {
	/** Куда вести ссылку из письма и возврат после Telegram, если не на эту же страницу (проект «Сцены» → /scene) */
	next?: string
}

/**
 * Вход и регистрация окном поверх страницы (владелец 11.10: гость пришёл по рекламной ссылке на тренд — «Регистрация»
 * не уводит с карточки, тренд остаётся за окном; потом — все кнопки входа гостя по разделам). Паттерн тот же, что у
 * тарифов (useOpenTariffs): страница едет в state.backgroundLocation, App рисует её под окном. ?next — место возврата
 * для письма подтверждения и Telegram. Из окна тарифов окно входа ложится над тарифами (их адрес и есть страница),
 * после входа шаг назад возвращает к тарифам.
 */
export function useOpenAuth() {
	const navigate = useNavigate()
	const location = useLocation()
	return useCallback(
		(path: AuthPath, opts?: AuthOpenOptions) => {
			const page = isAuthModalPath(location.pathname) ? (backgroundOf(location) ?? location) : location
			navigate(authUrl(path, opts?.next ?? page.pathname + page.search), { state: { backgroundLocation: page } })
		},
		[navigate, location],
	)
}

/**
 * Ссылка «Войти»/«Регистрация», открывающая окно: обычный клик — окно поверх страницы; клик с модификатором (новая
 * вкладка) и «Копировать адрес» — полный адрес с ?next, как раньше. onOpen — убрать то, что не должно остаться под
 * окном (меню на телефоне, окно создания аватара, мастер идей).
 */
export function useAuthLink() {
	const location = useLocation()
	const openAuth = useOpenAuth()
	return useCallback(
		(path: AuthPath, opts?: AuthOpenOptions & { onOpen?: () => void }) => ({
			href: authUrl(path, opts?.next ?? location.pathname + location.search),
			onClick: (e: MouseEvent<HTMLAnchorElement>) => {
				if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
				e.preventDefault()
				opts?.onOpen?.()
				openAuth(path, { next: opts?.next })
			},
		}),
		[location, openAuth],
	)
}

/**
 * Переходы между окнами входа (вход ↔ регистрация ↔ «Подтвердите email» ↔ восстановление пароля). В окне — подменяют
 * запись истории и держат подложку: крестик, Escape и «Назад» браузера возвращают на страницу одним шагом. На полной
 * странице — обычные переходы, как раньше.
 */
export function useAuthNav() {
	const navigate = useNavigate()
	const location = useLocation()
	const background = backgroundOf(location)
	const go = useCallback(
		(to: string) => {
			if (background) navigate(to, { replace: true, state: { backgroundLocation: background } })
			else navigate(to)
		},
		[navigate, background],
	)
	// Вход состоялся. На полной странице — уход на запомненный адрес. В окне место возврата — сама страница под ним:
	// окно закрывает себя само, как только появился вошедший пользователь (AuthRouteModal) — одно правило и для входа
	// в другой вкладке, и для «Вперёд» браузера после входа; запомненный адрес гасим, чтобы не сработал позже.
	const done = useCallback(() => {
		const dest = consumeReturn()
		if (!background) navigate(dest, { replace: true })
	}, [navigate, background])
	const link = useMemo(() => (background ? { state: { backgroundLocation: background }, replace: true } : {}), [background])
	return { inModal: !!background, go, done, link }
}

/**
 * Почту подтвердили в другой вкладке (ссылка из письма открывается новой): там уже вход, токены легли в общее
 * хранилище — подхватываем их и здесь, без перезагрузки. Окно входа (AuthRouteModal) после этого закрывается само,
 * экран «Проверьте почту» на полной странице ведёт дальше (VerifyPending). enabled=false — слушает кто-то другой.
 */
export function useAdoptOtherTabLogin(enabled = true) {
	const { login } = useAuth()
	const signingIn = useRef(false)
	useEffect(() => {
		if (!enabled) return
		const onStorage = (e: StorageEvent) => {
			if (e.key !== 'token' && e.key !== 'refresh_token') return
			const token = localStorage.getItem('token')
			if (!token) return
			api.setTokens(token, localStorage.getItem('refresh_token'))
			if (signingIn.current) return
			signingIn.current = true
			getProfile()
				.then(login)
				.catch(() => {
					signingIn.current = false
				})
		}
		window.addEventListener('storage', onStorage)
		return () => window.removeEventListener('storage', onStorage)
	}, [enabled, login])
}
